/**
 * One row in the vendors list (S14a) — list-view variant.
 *
 * Layout matches `prototype/purchases/vendors.html ▸ .vend-row` exactly:
 *
 *   ┌───┬─────────────────────────┬────────────┬─────────────┬──────────┬─────────┐
 *   │AVA│ name + health pill      │ activity   │ we owe      │ ytd      │ actions │
 *   │ 44│ meta (gstin · city · …) │ what + when│ value + sub │ value+s  │ + chev  │
 *   └───┴─────────────────────────┴────────────┴─────────────┴──────────┴─────────┘
 *
 * Plus a 3px LEFT-EDGE COLOURED RAIL keyed off vendor health
 * (rose=flag/awaiting OTP, amber=warn, emerald=good, slate=recurring).
 *
 * V1.0.x has the vendor master only. Columns that depend on bills /
 * vendor_payments / AI activity (health, activity, we-owe, YTD spend)
 * render as `—` placeholders; wiring lights up automatically when
 * those verticals land.
 *
 * @see prototype/purchases/vendors.html — V1.1 Studio Slate baseline
 */
import { Link } from "@tanstack/react-router";
import { ChevronRight, MessageCircle } from "lucide-react";
import type { vendors as contract } from "@flint/api-contract";
import type { z } from "zod";
import { StatusPill, cn } from "@flint/ui";
import { railClass, type RailTone } from "../../master-list/index.ts";
import { VendorAvatar } from "./vendor-avatar.tsx";
import { paiseToInrShort, formatDate } from "../../../lib/format.ts";

const formatPaiseShort = paiseToInrShort;
const formatShortDate = formatDate;

type ListRow = z.infer<typeof contract.listRow>;

export interface VendorRowProps {
  vendor: ListRow;
  className?: string;
}

export function VendorRow({ vendor, className }: VendorRowProps) {
  const isArchived = vendor.archivedAt !== null;
  // TODO V1.0.x — derive from bills + vendor_payments. For now: archived
  // = warn-tone, others = none (rail visible on hover only).
  const rail: RailTone = isArchived ? "warn" : "none";

  return (
    <Link
      to="/procurement/vendors/$vendorId"
      params={{ vendorId: vendor.id }}
      className={cn(
        "group relative grid items-center gap-3 md:gap-4 px-4 md:px-5 py-3.5 hover:bg-raised transition-colors",
        // Mobile-first: avatar / name+meta / chevron only.
        "grid-cols-[44px_minmax(0,1fr)_auto]",
        // md (≥768): adds the "We owe" column.
        "md:grid-cols-[44px_minmax(0,1fr)_110px_auto]",
        // lg (≥1024): full prototype grid — adds Activity + YTD columns.
        "lg:grid-cols-[44px_minmax(0,1.8fr)_minmax(0,1.2fr)_110px_110px_auto]",
        className,
      )}
    >
      {/* 3px left rail (per prototype `.vend-row::before`). */}
      <span aria-hidden className={railClass(rail)} />

      <VendorAvatar name={vendor.name} size="md" />

      {/* Name + meta */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="text-base font-bold text-ink truncate tracking-tight">
            {vendor.name}
          </span>
          {/* Health pill — Archived / Awaiting (has unpaid bills) /
              Active (has any bills) / No bills (master record only). */}
          {isArchived ? (
            <StatusPill tone="muted" size="xs" dot>
              Archived
            </StatusPill>
          ) : BigInt(vendor.outstandingPaise) > 0n ? (
            <StatusPill tone="warn" size="xs" dot>
              Awaiting
            </StatusPill>
          ) : vendor.billCount > 0 ? (
            <StatusPill tone="ok" size="xs" dot>
              Active
            </StatusPill>
          ) : (
            <StatusPill tone="muted" size="xs" dot>
              No bills
            </StatusPill>
          )}
        </div>
        {/* Meta: GSTIN · city · contact · TDS section */}
        <div className="text-xs text-muted truncate leading-snug">
          {vendor.gstin ? <span className="font-mono">{vendor.gstin}</span> : null}
          {vendor.city ? (
            <>
              {vendor.gstin ? " · " : ""}
              {vendor.city}
            </>
          ) : null}
          {vendor.contactName ? (
            <>
              {vendor.gstin || vendor.city ? " · " : ""}contact {vendor.contactName}
            </>
          ) : null}
          {vendor.tdsSection ? (
            <>
              {vendor.gstin || vendor.city || vendor.contactName ? " · " : ""}TDS{" "}
              <span className="font-mono">{vendor.tdsSection}</span>
            </>
          ) : null}
          {!vendor.gstin && !vendor.city && !vendor.contactName && !vendor.tdsSection ? (
            <span className="italic">No identity details on file</span>
          ) : null}
        </div>
      </div>

      {/* Activity column — real lastActivityAt from MAX(bills.received_on). */}
      <div className="hidden lg:flex flex-col gap-1 min-w-0" aria-label="Last activity">
        {vendor.billCount > 0 ? (
          <>
            <span className="text-xs text-ink-2 truncate">Bill received</span>
            <span className="text-xs text-muted font-mono">
              {vendor.lastActivityAt ? formatShortDate(vendor.lastActivityAt) : "—"}
            </span>
          </>
        ) : (
          <>
            <span className="text-xs text-ink-2 truncate italic">No activity yet</span>
            <span className="text-xs text-muted font-mono">—</span>
          </>
        )}
      </div>

      {/* "We owe" column — sum of bills in review/approval_pending/ready_to_pay. */}
      <div className="hidden md:flex flex-col gap-1 text-right tabular-nums" aria-label="We owe">
        <span className={`text-base font-bold font-mono ${BigInt(vendor.outstandingPaise) > 0n ? "text-ink" : "text-muted"}`}>
          {formatPaiseShort(vendor.outstandingPaise)}
        </span>
        <span className="text-xs text-muted">
          {vendor.billCount === 0
            ? "no bills"
            : `${vendor.billCount} ${vendor.billCount === 1 ? "bill" : "bills"}`}
        </span>
      </div>

      {/* YTD spend column — sum of paid bills in current Indian FY. */}
      <div className="hidden md:flex flex-col gap-1 text-right tabular-nums" aria-label="YTD spend">
        <span className="text-sm font-semibold text-ink-2 font-mono">
          {formatPaiseShort(vendor.ytdSpentPaise)}
        </span>
        <span className="text-xs text-muted">
          YTD · {vendor.billCount} {vendor.billCount === 1 ? "bill" : "bills"}
        </span>
      </div>

      {/* Actions: contextual icon button (placeholder) + chevron. */}
      <div className="flex items-center gap-1 justify-end">
        <button
          type="button"
          aria-label="Message vendor"
          title="Vendor messaging wires up with the notifications module"
          disabled
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          className="hidden md:inline-flex items-center justify-center h-8 w-8 rounded-sm border border-line bg-paper text-muted cursor-not-allowed disabled:opacity-50"
        >
          <MessageCircle size={13} strokeWidth={2} aria-hidden />
        </button>
        <span
          aria-hidden
          className="inline-flex items-center justify-center h-6 w-6 text-muted group-hover:text-ink-2 transition-colors"
        >
          <ChevronRight size={14} strokeWidth={2} />
        </span>
      </div>
    </Link>
  );
}
